
import Tabs from "@/components/Tabs/Tabs.vue";
import { TEmployeeName } from "@/models/employee-name";
import { TTab } from "@/models/tabs";
import { SMApp, SMUser } from "@/store";
import { API_ROUTES } from "@/utils/API";
import { Component, Vue, Watch } from "vue-property-decorator";
import { employerEmployeeTemplateLinks } from "./REmployeesTemplate";

const EXPERT_COMPANIES_PATH = "/expert/companies/";

@Component({
  components: {
    Tabs,
  },
})
export default class Company extends Vue {
  get employeeId(): number {
    return Number(this.$route.params.id ?? 0);
  }

  get companyId(): number {
    return Number(this.$route.params.company_id ?? 0);
  }

  get basePath() {
    return SMUser.basePath;
  }

  get tabs(): TTab[] {
    return employerEmployeeTemplateLinks.map(({ path, label }) => {
      const fullPath =
        SMUser.isExpert && this.companyId
          ? `${SMUser.basePath}/companies/${this.companyId}/employees/${this.employeeId}${path}`
          : `${SMUser.basePath}/employees/${this.employeeId}${path}`;

      return {
        path: fullPath,
        label: label.toUpperCase(),
        isActive: this.$route.path.includes(path),
      };
    });
  }

  mounted() {
    if (this.employeeName || this.companyName) this.setPageTitle();
  }

  updated() {
    if (this.employeeName || this.companyName) this.setPageTitle();
  }

  get employeeName() {
    return SMApp.employeeName;
  }

  @Watch("employeeName", { immediate: true })
  employeeNameChanged() {
    this.fetchEmployeeName();
  }

  get companyName() {
    return SMApp.companyName;
  }

  @Watch("companyName", { immediate: true })
  companyNameChanged(name: string) {
    if (!name) this.fetchCompanyName();
  }

  async fetchCompanyName() {
    if (!this.companyId) return;

    const { data } = await this.axios.get<string>(API_ROUTES.EXPERT_COMPANIES_NAME(this.companyId));

    if (!data) return;

    SMApp.setCompanyName(data);

    await this.setPageTitle();
  }

  async setPageTitle() {
    await this.$nextTick();

    const name = this.companyId
      ? `${this.$root.$tc("modules.names.companies")} > ${this.companyId} ${this.companyName}`
      : `${this.$root.$tc("modules.names.employees")}`;

    await this.$nextTick();

    SMApp.setPageTitle(name);

    const sectionName = ` > ${this.employeeName}`;

    SMApp.setPageSection(sectionName);

    if (this.companyId) {
      SMApp.setPageTitleLink(`${EXPERT_COMPANIES_PATH}${this.companyId}/summary`);
    } else {
      SMApp.setPageTitleLink(`${SMUser.basePath}/employees`);
    }
  }

  loading = false;

  get employeeNameEndpoint() {
    const { companyId, employeeId } = this;

    if (!employeeId) return "";

    if (companyId) return API_ROUTES.EXPERT_COMPANIES_EMPLOYEE_NAME_BY_ID(companyId, employeeId);

    return API_ROUTES.EMPLOYEE_NAME_BY_ID(employeeId);
  }

  async fetchEmployeeSummary() {
    if (!this.employeeNameEndpoint) return;

    const { data } = await this.axios.get(this.employeeNameEndpoint);

    if (!data) return;

    const employeeName = `${data.first_name} ${data.last_name}`;

    SMApp.setEmployeeName(employeeName);

    await this.setPageTitle();
  }

  async fetchEmployeeName() {
    if (!this.employeeNameEndpoint) return;

    try {
      this.loading = true;

      const { data } = await this.axios.get<TEmployeeName>(this.employeeNameEndpoint);

      if (!data) return;

      const employeeName = `${data.first_name} ${data.last_name}`;

      SMApp.setEmployeeName(employeeName);

      SMApp.setEmployeeWorkingStatus(data.employee_working_status);

      await this.setPageTitle();
    } finally {
      this.loading = false;
    }
  }
}
